import { Component, OnInit, OnDestroy, Input, inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { AvatarModule } from 'primeng/avatar';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { AllChangesDialogComponent } from './all-changes-dialog/all-changes-dialog.component';
import { ViewChangesService } from './view-changes.service';
import { RepoManagementService } from '../repo-management.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GlobalConstants } from 'src/app/base/global.constants';
import { UtilService } from 'src/app/services/util.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { metaInfo } from 'src/app/STORE/selectors/applifecycle.selector';
import {
  ComponentChange,
  ComponentChangeDetail,
  ViewChangesFilters,
  DEFAULT_VIEW_CHANGES_FILTERS
} from './view-changes.types';

@Component({
  selector: 'vb-view-changes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    AvatarModule,
    MultiSelectModule,
    DatePickerModule,
    FloatLabelModule,
    ProgressSpinnerModule,
    TooltipModule,
    AllChangesDialogComponent,
    TranslateModule
  ],
  templateUrl: './view-changes.component.html',
  styleUrls: ['./view-changes.component.scss']
})
export class ViewChangesComponent implements OnInit, AfterViewInit, OnDestroy {
  // Injected services
  private readonly viewChangesService = inject(ViewChangesService);
  private readonly repoService = inject(RepoManagementService);
  private readonly translate = inject(TranslateService);
  readonly utilService = inject(UtilService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly store = inject(Store);

  private metaInfoSub: Subscription | null = null;

  // ViewChild for scroll container
  @ViewChild('tableContainer') tableContainer!: ElementRef<HTMLElement>;

  // Date format constants
  readonly hourFormat = GlobalConstants.hourFormat;
  readonly dateFormat = GlobalConstants.dateFormat;
  readonly defaultDateTimeDisplayFormat = GlobalConstants.defaultDateTimeDisplayFormat;

  // Input Properties
  @Input() branchId = '';
  @Input() suiteMode = false;

  // Data arrays
  componentChanges: ComponentChange[] = [];
  total = 0;
  appNameMap: Record<string, string> = {};

  filterApplicationIds: string[] = [];
  applicationNameOptions: { label: string; value: string }[] = [];


  // Search and filter
  filterComponentTypes: string[] = [];
  startDate: Date | null = null;
  endDate: Date | null = null;
  filters: ViewChangesFilters = { ...DEFAULT_VIEW_CHANGES_FILTERS };
  

  get hasActiveFilters(): boolean {
    return (this.filterComponentTypes?.length ?? 0) > 0 ||
           (this.filterApplicationIds?.length ?? 0) > 0 ||
           this.startDate !== null ||
           this.endDate !== null;
  }

  // Sorting
  sortField = 'componentType';
  sortOrder: 'asc' | 'desc' = 'desc';
  hasUserSorted = false;

  // Pagination
  page = 1;
  pageSize = GlobalConstants.defaultPageSize;
  hasMore = true;
  loading = false;
  initializing = true;

  // All changes dialog
  showAllChangesDialog = false;
  selectedComponent: ComponentChange | null = null;
  selectedComponentChanges: ComponentChangeDetail[] = [];
  dialogLoading = false;

  // Filter options
  componentTypeOptions: { label: string; value: string }[] = [];

  // Branch-specific metaInfo map (componentId -> name) fetched via API
  private branchMetaInfoMap: Record<string, string> = {};

  constructor() {
    console.warn('ViewChangesComponent CONSTRUCTOR called!');
  }

  ngOnInit(): void {
    // Subscribe to appmetaInfo store changes so user IDs resolve to names
    this.subscribeToMetaInfoChanges();
    // Fetch branch-specific appMetaInfo first, then load data
    this.fetchBranchMetaInfo();
  }

  ngAfterViewInit(): void {
    // Attach scroll listener to p-table's internal scroll wrapper after view initializes
    setTimeout(() => this.attachInfiniteScroll(), 300);
  }

  private attachInfiniteScroll(): void {
    if (!this.tableContainer?.nativeElement) {
      return;
    }

    // Find the p-table's scrollable wrapper element
    const scrollWrapper = this.tableContainer.nativeElement.querySelector(
      '.p-datatable-wrapper, .p-datatable-table-container'
    );

    if (scrollWrapper) {
      scrollWrapper.addEventListener('scroll', () => {
        const element = scrollWrapper as HTMLElement;
        // Check if scrolled to bottom (with 1px buffer)
        if (element.offsetHeight + element.scrollTop + 1 >= element.scrollHeight) {
          this.loadMore();
        }
      });
    }
  }

  onDateFilterChange(): void {
    // Trigger filter when the datepicker panel closes
    this.onFilterChange();
  }

  // Load data and filter options in a single API call
  loadDataWithFilters(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.page = 1;
    this.componentChanges = [];
    const filters = this.buildFilters();

    this.viewChangesService
      .getComponentChangesWithFilters({
        branchId: this.branchId,
        filters: filters,
        page: this.page,
        length: this.pageSize,
        suiteMode: this.suiteMode
      })
      .subscribe({
        next: (response) => {
          this.componentChanges = response.data.filter((c) => c.componentType !== 'RUNTIME_CONFIGURATION');
           if (this.suiteMode && response.appNameMap) {
            this.appNameMap = response.appNameMap;
            this.buildApplicationFilterOptions();
          }

          this.enrichComponentDisplayNames();
          this.rebuildFilterOptions();
          this.total = response.total;
          this.hasMore = this.page * this.pageSize < response.total;
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading component changes with filters:', err);
          this.loading = false;
        }
      });
  }

  /**
   * Build application filter options from the appNameMap returned by the API.
   */
  private buildApplicationFilterOptions(): void {
    this.applicationNameOptions = Object.entries(this.appNameMap).map(([appId, appName]) => ({
      label: appName,
      value: appId
    }));
  }

  private buildFilters(): ViewChangesFilters {
    const filters: ViewChangesFilters = {};

    if (this.filterComponentTypes?.length > 0) {
      filters.componentType = this.filterComponentTypes;
    }

    if (this.filterApplicationIds?.length > 0) {
      filters.appIdFilter = this.filterApplicationIds;
    }

    // Add date filters directly - send null for empty dates
    if (this.startDate) {
      filters.dateFrom = this.startDate.toISOString();
    }
    if (this.endDate) {
      filters.dateTo = this.endDate.toISOString();
    }

    return filters;
  }
  

  loadData(append = false): void {
    if (this.loading) {
      return;
    }

    if (!append) {
      this.page = 1;
      this.componentChanges = [];
    }

    this.loading = true;
    const filters = this.buildFilters();

    // Build params with optional sort
    const params: { branchId: string; filters: ViewChangesFilters; page: number; length: number; sort?: { field: string; direction: string }; suiteMode?: boolean } = {
      branchId: this.branchId,
      filters: filters,
      suiteMode: this.suiteMode,
      page: this.page,
      length: this.pageSize
    };

    // Add sort only if user has explicitly sorted
    if (this.hasUserSorted) {
      params.sort = { field: this.sortField, direction: this.sortOrder.toUpperCase() };
    }

    this.viewChangesService
      .getComponentChanges(params).subscribe({
        next: (response) => {
        if (append) {
          this.componentChanges = [...this.componentChanges, ...response.data.filter((c) => c.componentType !== 'RUNTIME_CONFIGURATION')];
          } else {
            this.componentChanges = response.data.filter((c) => c.componentType !== 'RUNTIME_CONFIGURATION');
          }
          this.enrichComponentDisplayNames();
          this.rebuildFilterOptions();
          this.total = response.total;
          this.hasMore = (this.page * this.pageSize) < response.total;
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading component changes:', err);
          this.loading = false;
        }
    });
  }

  /**
   * Rebuild filter dropdown options from the currently loaded table data.
   * Accumulates options across pages without losing previously seen values.
   */
  private rebuildFilterOptions(): void {
    const mergeOptions = (
      existing: { label: string; value: string }[],
      newValues: string[],
      labelFn?: (v: string) => string
    ): { label: string; value: string }[] => {
      const map = new Map<string, string>();
      existing.forEach((opt) => map.set(opt.value, opt.label));
      newValues.forEach((v) => {
        if (v && !map.has(v)) {
          map.set(v, labelFn ? labelFn(v) : v);
        }
      });
      return Array.from(map.entries()).map(([value, label]) => ({ label, value }));
    };

    // Component type options – translate labels
    const componentTypes = this.componentChanges
      .map((c) => c.componentType)
      .filter((v): v is string => !!v);
    this.componentTypeOptions = mergeOptions(
      this.componentTypeOptions,
      componentTypes,
      (v) => this.utilService.formatComponentType(v)
    );

  }

  onFilterChange(): void {
    this.loadData();
  }

  /**
   * Get label for a selected filter value
   */
  getFilterLabel(field: 'componentType', value: string): string {
    const option = this.componentTypeOptions.find((opt) => opt.value === value);
    return option?.label || this.utilService.formatComponentType(value);
  }

  clearFilters(): void {
    this.filterComponentTypes = [];
    this.filterApplicationIds = [];
    this.startDate = null;
    this.endDate = null;
    this.loadData();
  }

  onSort(field: string): void {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortOrder = 'desc';
    }
    this.hasUserSorted = true;
    this.loadData();
  }

  loadMore(): void {
    if (this.hasMore && !this.loading) {
      this.page++;
      this.loadData(true);
    }
  }

  openAllChangesDialog(component: ComponentChange): void {
    this.selectedComponent = component;
    this.showAllChangesDialog = true;
    this.selectedComponentChanges = [];
    this.dialogLoading = true;

    // In suite mode, use the row's appId; otherwise use the single branchId
    const targetBranchId = this.suiteMode && component.appId ? component.appId : this.branchId;

    this.viewChangesService
      .getComponentChangeDetails(component.componentId, targetBranchId, component.componentType)
      .subscribe({
      next: (response) => {
        this.selectedComponentChanges = response.data || [];
        if (this.selectedComponentChanges.length > 0) {
          this.selectedComponentChanges.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
        }
        this.dialogLoading = false;
      },
      error: (err) => {
        console.error('Error fetching component change details:', err);
        this.selectedComponentChanges = [];
        this.dialogLoading = false;
      }
    });
  }

  onAllChangesDialogClose(): void {
    this.showAllChangesDialog = false;
    this.selectedComponent = null;
    this.selectedComponentChanges = [];
  }

  /**
   * Fetch branch-specific appMetaInfo via API, then load component changes data.
   */
  private fetchBranchMetaInfo(): void {
    this.initializing = true;
    this.viewChangesService.getBranchMetaInfo(this.branchId).subscribe({
      next: (response: { results?: { modelId: string; name: string }[] }) => {
        const results = response?.results || [];
        results.forEach((rec: { modelId: string; name: string }) => {
          if (rec.modelId && rec.name) {
            this.branchMetaInfoMap[rec.modelId] = rec.name;
          }
        });
        this.initializing = false;
        this.loadDataWithFilters();
      },
      error: () => {
        // Even if meta info fetch fails, still load the data
        this.initializing = false;
        this.loadDataWithFilters();
      }
    });
  }

  /**
   * Enrich all component changes with resolved display names.
   * componentId is resolved to a human-readable name via the branch-specific metaInfo fetched from API.
   * User IDs (updatedUserId, creatorId, deleteUserId, renamedUserId) are resolved to
   * display names via the global appmetaInfo store (populated by Firestore listener).
   */
  private readonly componentTypesWithoutMeta = new Set([
    'TECHNOLOGY_CHOICES', 'APP_LANGUAGES', 'QUERYVIEW_CUD_CONFIGURATION',
    'APP_SETTINGS', 'APPLICATION_CONFIGURATION', 'SEARCHABLE_INDEX_FIELD',
    'RUNTIME_CONFIGURATION', 'MAIL_INFO', 'ATTACHMENT', 'WORKFLOW_ACTION',
    'WORKFLOW_ACTOR', 'WORKFLOW_MAIL_INFO', 'TEMPLATE', 'TRANSLATION'
  ]);

  private resolveUserNamesForChange(change: ComponentChange, userMetaInfo: Record<string, string>): void {
    if (change.updatedUserId) {
      change.updated.author = this.utilService.normalizeDisplayName(userMetaInfo[change.updatedUserId] || change.updatedUserId);
    }
    if (change.creatorId) {
      change.created.author = this.utilService.normalizeDisplayName(userMetaInfo[change.creatorId] || change.creatorId);
    }
    if (change.deleteUserId && change.deleted) {
      change.deleted.author = this.utilService.normalizeDisplayName(userMetaInfo[change.deleteUserId] || change.deleteUserId);
    }
    if (change.renamedUserId && change.renamed) {
      change.renamed.author = this.utilService.normalizeDisplayName(userMetaInfo[change.renamedUserId] || change.renamedUserId);
    }
  }

  private enrichComponentDisplayNames(): void {
    if (!this.componentChanges?.length) {
      return;
    }

    const userMetaInfo = this.appGlobalService.get('appmetaInfo') || {};

    for (const change of this.componentChanges) {
      change.componentDisplayName = this.componentTypesWithoutMeta.has(change.componentType)
        ? String(this.translate.instant(change.componentType))
        : (this.branchMetaInfoMap[change.componentId] || change.componentId);

      this.resolveUserNamesForChange(change, userMetaInfo);

      if (this.suiteMode && change.appId && this.appNameMap[change.appId]) {
        change.applicationName = this.appNameMap[change.appId];
      }
    }
  }

  /**
   * Subscribe to ngrx metaInfo store changes. When the Firestore listener in header
   * updates appmetaInfo, re-enrich displayed data so user IDs resolve to names.
   */
  private subscribeToMetaInfoChanges(): void {
    this.metaInfoSub = this.store.select(metaInfo).subscribe(() => {
      this.enrichComponentDisplayNames();
    });
  }

  ngOnDestroy(): void {
    if (this.metaInfoSub) {
      this.metaInfoSub.unsubscribe();
    }
  }

  getBgColor(str: string): string {
    return this.repoService.getBgColor(str);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    // Check if scrolled to bottom (with 1px buffer for reliability)
    if (element.offsetHeight + element.scrollTop + 1 >= element.scrollHeight) {
      this.loadMore();
    }
  }

  trackByDateTime(index: number, item: ComponentChangeDetail): string {
    return item.dateTime;
  }

  trackByComponentName(index: number, item: ComponentChange): string {
    return item.componentName;
  }

}
